import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";

import { apiRoutes } from "../../../configs/apiRoutes";
import instance from "../../../api/apiClient";
import sanitizeFileName from "helpers/sanitizeFileName";
import { mergeExistingFields } from "helpers/mergeExistingFields";
import { ApiErrorPayload } from "types/ApiErrorPayload";

export interface GameItem {
  id: number;
  title: string;
  description: string;
  slug: string;
  isActive: string;
  isTop: string;
  isVip: string;
  isNew: boolean;
  isPopular: boolean;
  isExclusive: boolean;
  isTrending: boolean;
  isHidden: string;
  isDeactivated: string;
  hasDemo: string;
  hasFreeSpins: string;
  isMobile: string;
  devices: string[];
  type: string;
  technology: string;
  rtp: string;
  reelsCount: string;
  linesCount: string;
  volatility: string;
  image?: string | File;
  images?: {
    portrait?: string | File;
    landscape?: string | File;
    square?: string | File;
  };
  uuid?: string;
  provider: {
    id: number;
    title: string;
    description: string;
    slug: string;
  };
}

export const mergeGameItem = (
  current: GameItem,
  updated: GameItem,
): GameItem =>
  mergeExistingFields(current, {
    ...updated,
    image:
      updated.image === undefined || updated.image instanceof File
        ? current.image
        : updated.image,
    images: {
      ...current.images,
      ...updated.images,
      portrait:
        updated.images?.portrait === undefined ||
        updated.images.portrait instanceof File
          ? current.images?.portrait
          : updated.images.portrait,
      landscape:
        updated.images?.landscape === undefined ||
        updated.images.landscape instanceof File
          ? current.images?.landscape
          : updated.images.landscape,
      square:
        updated.images?.square === undefined ||
        updated.images.square instanceof File
          ? current.images?.square
          : updated.images.square,
    },
  });

interface GamesState {
  list: GameItem[];
  item?: GameItem;
  activePage: number;
  totalPages: number;
  totalItems: number;
  isListFetched: boolean;
  isItemFetched: boolean;
  selectedItems: number[];
  processingItemId?: number;
}
interface GetListPayload {
  limit: number;
  offset: number;
  isActive?: number;
  isHidden?: number;
  isNew?: number;
  hasCategory?: number;
  isDeactivated?: number;
  providerIds?: number[];
  collectionIds?: number[];
  tagIds?: number[];
  geos?: string[];
  gameTitle?: string;
  type?: string;
  hasFreeSpins?: number;
  order: {
    id: string;
    slug: string;
    title: string;
    providerTitle: string;
  };
}
interface UpdateGamesPayload {
  gameIds: number[];
  isActive?: number;
  isDeactivated?: number;
  isMobile?: number;
  hasFreeSpins?: number;
  hasDemo?: number;
  isTop?: number;
  isVip?: number;
  isNew?: number;
  isPopular?: number;
  isExclusive?: number;
  isTrending?: number;
}

export const updateGamesBunch = createAsyncThunk(
  "game-center/games/update-bunch",
  async (data: UpdateGamesPayload) => {
    try {
      await instance.put(`${apiRoutes.gameBunchUpdate}`, data);
      return true;
    } catch (error) {
      return false;
    }
  },
);

export const getGamesList = createAsyncThunk(
  "game-center/games/list",
  async (data: GetListPayload) => {
    try {
      const result = await instance.post(`${apiRoutes.gamesList}`, { ...data });
      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);

export const getGamesItem = createAsyncThunk(
  "game-center/games/item",
  async (id: number | string) => {
    try {
      const result = await instance.get(`${apiRoutes.gameRead}/${id}`);
      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);
export const updateGame = createAsyncThunk<
  GameItem,
  GameItem,
  { rejectValue: ApiErrorPayload }
>(
  "game-center/games/update",
  async (payload: GameItem, { rejectWithValue }) => {
    const data = new FormData();
    if (payload.image instanceof File) {
      const safeName = sanitizeFileName(payload.image.name);

      data.append(
        "image",
        new File([payload.image], safeName, { type: payload.image.type }),
      );
    }
    if (payload.images?.portrait instanceof File) {
      data.append("imagePortrait", payload.images.portrait);
    }

    if (payload.images?.landscape instanceof File) {
      data.append("imageLandscape", payload.images.landscape);
    }

    if (payload.images?.square instanceof File) {
      data.append("imageSquare", payload.images.square);
    }
    data.append("title", (payload?.title as string) ?? "");
    data.append("slug", (payload.slug as string) ?? "");
    if (payload.description)
      data.append("description", (payload.description as string) ?? "");
    data.append("type", (payload.type as string) ?? "");
    data.append("isActive", payload.isActive ? "1" : "0");
    data.append("isDeactivated", payload.isDeactivated ? "1" : "0");
    data.append("isMobile", payload.isMobile ? "1" : "0");
    data.append("hasDemo", payload.hasDemo ? "1" : "0");
    data.append("isTop", payload.isTop ? "1" : "0");
    data.append("isVip", payload.isVip ? "1" : "0");
    data.append("isNew", payload.isNew ? "1" : "0");
    data.append("isPopular", payload.isPopular ? "1" : "0");
    data.append("isExclusive", payload.isExclusive ? "1" : "0");
    data.append("isTrending", payload.isTrending ? "1" : "0");
    data.append("hasFreeSpins", payload.hasFreeSpins ? "1" : "0");
    try {
      await instance.put(`${apiRoutes.gameUpdate}/${payload.id}`, data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      try {
        const result = await instance.get(
          `${apiRoutes.gameRead}/${payload.id}`,
        );
        return result.data ?? payload;
      } catch {
        return payload;
      }
    } catch (error: any) {
      const data = error?.data;
      const apiError = data?.error ?? data;

      if (apiError && apiError.code) {
        return rejectWithValue(apiError);
      }

      return rejectWithValue({
        code: "InternalError",
        fieldErrors: [],
        globalErrors: [{ code: "unknownError" }],
      });
    }
  },
);

const initialState: GamesState = {
  list: [],
  isListFetched: false,
  isItemFetched: false,
  activePage: 1,
  totalItems: 0,
  totalPages: 0,
  selectedItems: [],
  processingItemId: undefined,
};

export const gamesSlice = createSlice({
  name: "games",
  initialState,
  reducers: {
    handlePaginate: (state: GamesState, action: PayloadAction<number>) => {
      state.activePage = action.payload;
      state.isListFetched = false;
      state.selectedItems = [];
    },
    handleSelectItem: (state: GamesState, action: PayloadAction<number>) => {
      if (state.selectedItems.includes(action.payload)) {
        state.selectedItems = state.selectedItems.filter(
          (el) => el !== action.payload,
        );
      } else {
        state.selectedItems = [...state.selectedItems, action.payload];
      }
    },
    handleSelectAll: (state: GamesState) => {
      if (state.selectedItems.length === state.list.length) {
        state.selectedItems = [];
      } else {
        state.selectedItems = state.list.map((el) => el.id);
      }
    },
    handleClearItem: (state: GamesState) => {
      state.item = undefined;
      state.isItemFetched = false;
    },
    handleUpdateItem: (
      state: GamesState,
      action: PayloadAction<Partial<GameItem>>,
    ) => {
      state.item = { ...(state.item || {}), ...action.payload } as GameItem;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getGamesList.fulfilled, (state, action) => {
      state.isListFetched = true;
      state.list = action.payload?.items ?? [];
      state.totalItems = action.payload?.totalItems ?? 1;
      state.totalPages = action.payload?.totalPages ?? 1;
    });
    builder.addCase(getGamesItem.pending, (state) => {
      state.item = undefined;
      state.isItemFetched = false;
    });
    builder.addCase(getGamesItem.fulfilled, (state, action) => {
      state.item = action.payload;
      state.isItemFetched = Boolean(action.payload);
    });
    builder.addCase(updateGame.pending, (state, action) => {
      state.processingItemId = action.meta.arg.id;
    });
    builder.addCase(updateGame.fulfilled, (state, action) => {
      state.list = state.list.map((item) =>
        item.id === action.payload.id ? mergeGameItem(item, action.payload) : item,
      );
      state.processingItemId = undefined;
    });
    builder.addCase(updateGame.rejected, (state) => {
      state.processingItemId = undefined;
    });
    builder.addCase(updateGamesBunch.fulfilled, (state) => {
      state.isListFetched = false;
      state.selectedItems = [];
    });
  },
});

export const {
  handlePaginate,
  handleSelectItem,
  handleSelectAll,
  handleClearItem,
  handleUpdateItem,
} = gamesSlice.actions;
